import apiV2, { apiV1 } from "api-v2";
import * as constants from "./actionTypes";
import { toFormData } from "axios";
import { grabFirstError, processFormData, showToast } from "utils/helpers";
import {
	endPerformance,
	logErrorIfDebug,
	logPerformance,
	startPerformance,
} from "utils/logging";

export const getRfqPerPage = ({
	limit = 12,
	page = 1,
	search = "",
	sort = "last_activity",
	filters,
	reset = false,
}) => {
	return async (dispatch, getState) => {
		dispatch({ type: constants.GET_RFQS_REQUEST });
		if (reset) {
			dispatch({ type: constants.RESET_RFQ, payload: true });
		}
		(async () => {
			try {
				const params = { limit, page, search, sort, filters };
				const {
					data: { data: rfqsData, filters: filterOptions },
				} = await apiV2.get(`rfq`, { params });

				let oldData = await getState().rfq.rfqs.rfqs;
				let newOldData = { ...oldData };

				if (page > 1) {
					Object.keys(rfqsData).forEach((rfq) => {

						const oldDataValue = oldData[rfq];

						if (!oldDataValue) {
							return;
						}

						if (
							oldDataValue.meta.last_page >= rfqsData[rfq].meta.current_page
						) {
							newOldData[rfq].data = [
								...(oldDataValue?.data ?? []),
								...(rfqsData[rfq]?.data ?? []),
							];
							newOldData[rfq].meta = rfqsData[rfq].meta;
						}
					});
				}

				let countData = { ...(await getState().rfq.rfqs.rfqCount) };
				Object.keys(rfqsData).forEach((rfq) => {
					countData[rfq] = rfqsData[rfq].meta.total;
				});

				if (reset) {
					await dispatch({
						type: constants.GET_RFQS_SUCCESS,
						payload: rfqsData,
					});
					await dispatch({
						type: constants.GET_RFQ_FILTER_OPTIONS,
						payload: filterOptions,
					});
				} else {
					await dispatch({
						type: constants.GET_RFQS_SUCCESS,
						payload: page > 1 ? newOldData : rfqsData,
					});
					await dispatch({
						type: constants.SET_RFQ_BADGES_COUNT,
						payload: countData,
					});
					await dispatch({
						type: constants.GET_RFQ_FILTER_OPTIONS,
						payload: filterOptions,
					});
				}
			} catch (err) {
				logErrorIfDebug(err);
				showToast(
					"Unexpected error whilst processing Rfx Data, please try again.",
					"Error",
					false
				);
			}
			//for future use
			// await dispatch({ type: constants.IS_SEARCHING, payload: false });
		})();
	};
};

export const getRFQId = async (id) => {
	const startTime = startPerformance();
	try {
		const { data: rfq } = await apiV2.get(`rfq/${id}`);
		if (rfq.status) {
			return rfq.data;
		} else {
			return null;
		}
	} catch (err) {
		return null;
	} finally {
		const endTime = endPerformance();
		logPerformance(startTime, endTime, "getRfxById");
	}
};

export const getRFQType = async () => {
	try {
		const { data: rfq } = await apiV2.get(`rfq/type`);
		if (rfq.status) {
			return rfq.data;
		} else {
			return null;
		}
	} catch (err) {
		throw new Error(err?.response?.data?.message);
	}
};

export const getDocumentType = async () => {
	try {
		const { data: categories } = await apiV2.get(`rfq/categories`);
		if (categories.status) {
			return categories.data;
		} else {
			return null;
		}
	} catch (err) {
		throw new Error(err?.response?.data?.message);
	}
};

export const saveDraftRFQ = async (rfqId, data, step, isSubmit, actions) => {
	const formData = toFormData({ ...data, step }, undefined, { indexes: true });
	const failMsg = isSubmit
		? "Failed to submit RFx. "
		: "Draft RFx save failed. ";
	try {
		const { data: saved } = await apiV2.post(
			`rfq/${rfqId}/${isSubmit ? "submit" : "save"}`,
			formData
		);
		if (saved.status) {
			// The final submit confirms itself with the InvitationSentModal, so the
			// generic save toast would double up there. Draft saves keep it.
			if (!isSubmit) {
				showToast(null, "Rfx Changes Saved Successfully", true);
			}
			return saved.data;
		} else {
			showToast(failMsg, saved?.title, false, failMsg);
			return null;
		}
	} catch ({ response }) {
		response?.data?.errors &&
			actions &&
			actions.setErrors(response?.data?.errors);

		showToast(
			failMsg + (grabFirstError(response?.data?.errors) || ""),
			response?.data?.title,
			false,
			failMsg
		);
		throw Error(response);
	}
};

export const updateRFXSupplierList = async (rfqId, supplierListId) => {
	try {
		const { data: response } = await apiV2.post(
			`/rfq/${rfqId}/update-supplier-list`,
			{
				supplier_list_id: supplierListId,
			}
		);

		if (response.status) {
			showToast(response?.message, response?.title, true);
			return response.data;
		} else {
			showToast(response?.message, response?.title);
			return null;
		}
	} catch (error) {
		const errorData = error?.response?.data;

		// Handle 422 validation errors
		if (error?.response?.status === 422 && errorData?.errors) {
			const firstError = Object.values(errorData.errors)[0];
			const errorMessage = Array.isArray(firstError)
				? firstError[0]
				: firstError;
			showToast(errorMessage, errorData?.title || "Validation Error");
		} else {
			showToast(errorData?.message || "An error occurred", "Error");
		}
		throw error;
	}
};

export const setAwardDeclineSupplier = async (id, data) => {
	let isGuest = data?.email || isNaN(data?.company) ? 1 : 0;

	let payload = {
		company_id: !data?.email ? data?.company : null,
		isGuest: isGuest,
		status: data?.status,
		document: data?.file?.length > 0 ? data?.file[0] : null,
		message: data?.message,
		email: data?.email || data?.company,
	};
	const formData = processFormData(payload);
	try {
		const { data: send } = await apiV2.post(`rfq/${id}/awarded`, formData);
		if (send.status) {
			showToast(send?.message, send?.title, true);
			return true;
		}
	} catch (err) {
		showToast(
			err?.response?.data?.message ||
			err?.response?.data?.title ||
			err?.message,
			"Error"
		);
	}
};

export const getRFQFilteringOptions = async () => {
	try {
		const { data: options } = await apiV2.get(`rfq/rfq-filter-attribute`);
		if (options.status) {
			return options.data;
		} else {
			return null;
		}
	} catch (err) {
		throw new Error(err?.response?.data?.message);
	}
};

export const updateSubmissionDeadLine = async (rfqID, data) => {
	try {
		const response = await apiV2.post(`rfq/${rfqID}/extend-deadline`, data);
		return response;
	} catch (error) {
		throw error;
	}
};

export const getDocumentCategories = async () => {
	try {
		const response = await apiV1.get(`document-categories`);
		return response.data;
	} catch (err) {
		throw new Error(
			err?.response?.data?.message || "Failed to fetch document categories"
		);
	}
};

export const saveProjectChangesForRfx = async (projectId, rfxId, values) => {
	try {
		const response = await apiV2.post(
			`/rfx/${rfxId}/project-experience/${projectId}`,
			values
		);
		if (response.status === 200) {
			showToast("Project changes saved successfully for this RFX", "success", true);
			return response.data;
		}
		throw new Error("Failed to save project changes for RFX");
	} catch (err) {
		showToast(
			err?.response?.data?.message || "Failed to save project changes",
			"error"
		);
		throw err;
	}
};
